import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTranslation } from 'react-i18next';
import ChartPlaceholder from './ChartPlaceholder';
import { createAdvancedChartBase } from './chartConfigUtils';

const currencyAxisLabelFormatter: Highcharts.AxisLabelsFormatterCallbackFunction = function () {
    return Highcharts.numberFormat(Number(this.value), 0, ',', ' ');
};

interface CashFlowData {
    readonly period: string;
    readonly expectedInflows: number;
    readonly expectedOutflows: number;
    readonly netCashFlow: number;
    readonly cumulativeCashPosition: number;
}

interface CashFlowChartProps {
    readonly data: readonly CashFlowData[];
    readonly loading?: boolean;
    readonly currency?: 'EUR' | 'LEI';
}

function CashFlowChart({
    data,
    loading = false,
    currency = 'EUR',
}: CashFlowChartProps): JSX.Element {
    const { t } = useTranslation();

    if (loading || data.length === 0) {
        return <ChartPlaceholder loading={loading} hasData={data.length > 0} />;
    }

    const currencyLabel = currency === 'LEI' ? 'RON' : 'EUR';

    const categories = data.map(d => d.period);
    const base = createAdvancedChartBase(
        'column',
        categories,
        t('statistics.advanced.period', 'Period'),
    );

    const options: Highcharts.Options = {
        ...base,
        yAxis: [
            {
                title: {
                    text: t('statistics.advanced.cashFlow', 'Cash Flow') + ` (${currencyLabel})`,
                },
                labels: {
                    formatter: currencyAxisLabelFormatter,
                },
            },
            {
                title: {
                    text:
                        t('statistics.advanced.cumulativePosition', 'Cumulative Position') +
                        ` (${currencyLabel})`,
                },
                labels: {
                    formatter: currencyAxisLabelFormatter,
                },
                opposite: true,
            },
        ],
        tooltip: {
            shared: true,
            valueDecimals: 0,
        },
        plotOptions: {
            column: {
                borderWidth: 0,
            },
        },
        series: [
            {
                type: 'column',
                name: t('statistics.advanced.inflows', 'Expected Inflows'),
                data: data.map(d => d.expectedInflows),
                color: '#52c41a',
            },
            {
                type: 'column',
                name: t('statistics.advanced.outflows', 'Expected Outflows'),
                data: data.map(d => -d.expectedOutflows), // Negative for visual
                color: '#ff4d4f',
            },
            {
                type: 'spline',
                name: t('statistics.advanced.netCashFlow', 'Net Cash Flow'),
                data: data.map(d => d.netCashFlow),
                color: '#1890ff',
                lineWidth: 2,
                dashStyle: 'ShortDash',
                yAxis: 0,
                marker: {
                    enabled: true,
                    radius: 4,
                },
            },
            {
                type: 'spline',
                name: t('statistics.advanced.cumulativePosition', 'Cumulative Position'),
                data: data.map(d => d.cumulativeCashPosition),
                color: '#722ed1',
                lineWidth: 3,
                yAxis: 1,
                marker: {
                    enabled: true,
                    radius: 5,
                    symbol: 'diamond',
                },
            },
        ],
        credits: {
            enabled: false,
        },
        legend: {
            enabled: true,
        },
    };

    return <HighchartsReact highcharts={Highcharts} options={options} />;
}

export default CashFlowChart;
